import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import {
    processHighTimeComponentData,
    buildHighTimeWaterfallChart,
    filterComponents,
    REMAINING_BAND_COLORS,
    REMAINING_BAND_LABELS,
    REMAINING_BAND_ORDER,
} from './highTimeComponentUtils';
import { aircraftComponentLifePath } from './aircraftComponentLifeUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const USAGE_REMAINING_MIN = 0;
const USAGE_REMAINING_STEP = 50;

const STATUS_COLORS = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bb',
    AWP: '#335d80',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d',
};

function getStatusColor(status) {
    return STATUS_COLORS[status] || '#6c757d';
}

function buildStatusByBuno(records) {
    const map = {};
    (records || []).forEach(ac => {
        if (ac.buno) {
            map[String(ac.buno).trim()] = ac.status || '';
        }
    });
    return map;
}

function formatHours(value) {
    if (value == null || value === '') return 'N/A';
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(1) : 'N/A';
}

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        section: {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px',
        },
        pageHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '10px',
        },
        navLink: {
            color: colors.link,
            textDecoration: 'none',
            fontSize: '16px',
        },
        card: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 8px 0',
        },
        subtitle: {
            fontSize: '14px',
            color: colors.subtleText,
            margin: '0 0 16px 0',
        },
        controls: {
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '180px',
            maxWidth: '100%',
        },
        componentSelect: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '280px',
            maxWidth: '100%',
            flex: '1 1 320px',
        },
        searchInput: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '200px',
        },
        button: {
            padding: '8px 16px',
            backgroundColor: colors.link,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
        },
        chartContainer: {
            position: 'relative',
            height: '480px',
            margin: '12px 0',
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
        },
        empty: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        legendWrap: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '16px',
        },
        legendItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            color: colors.text,
            fontSize: '14px',
        },
        legendSwatch: {
            width: '16px',
            height: '16px',
            borderRadius: '2px',
        },
        detailTable: {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '20px',
            fontSize: '13px',
        },
        tableWrap: {
            maxHeight: '400px',
            overflowY: 'auto',
            marginTop: '8px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
        },
        th: {
            textAlign: 'left',
            padding: '8px',
            borderBottom: `2px solid ${colors.border}`,
            color: colors.text,
            position: 'sticky',
            top: 0,
            backgroundColor: colors.cardBg,
        },
        td: {
            padding: '8px',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
        },
        zoomControl: {
            marginBottom: '16px',
            padding: '14px 16px',
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
        },
        zoomHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            flexWrap: 'wrap',
            gap: '8px',
        },
        zoomLabel: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: colors.text,
        },
        zoomValue: {
            fontSize: '14px',
            color: colors.link,
            fontWeight: 'bold',
        },
        zoomSlider: {
            width: '100%',
            height: '6px',
            cursor: 'pointer',
            accentColor: colors.link,
        },
        zoomScale: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: colors.subtleText,
            marginTop: '6px',
        },
        zoomHint: {
            fontSize: '12px',
            color: colors.subtleText,
            marginTop: '8px',
            fontStyle: 'italic',
        },
    };
};

function HighTimeComponentPage({ theme }) {
    const styles = getStyles(theme);
    const [loadingComponents, setLoadingComponents] = useState(true);
    const [loadingRows, setLoadingRows] = useState(false);
    const [siteFilter, setSiteFilter] = useState('');
    const [sites, setSites] = useState([]);
    const [components, setComponents] = useState([]);
    const [componentSearch, setComponentSearch] = useState('');
    const [selectedFamilyId, setSelectedFamilyId] = useState('');
    const [componentRows, setComponentRows] = useState([]);
    const [statusByBuno, setStatusByBuno] = useState({});
    const [usageMax, setUsageMax] = useState(null);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        api.get('/sites/')
            .then(res => setSites(res.data.results || res.data || []))
            .catch(err => console.error('Error fetching sites:', err));
    }, []);

    useEffect(() => {
        api.get('/aircraft-status/')
            .then(res => {
                const statuses = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setStatusByBuno(buildStatusByBuno(statuses));
            })
            .catch(err => console.error('Error fetching aircraft status:', err));
    }, []);

    const fetchComponents = useCallback(() => {
        setLoadingComponents(true);
        const params = new URLSearchParams();
        if (siteFilter) params.append('site_name', siteFilter);
        const qs = params.toString();

        api.get(`/crd-all/components/${qs ? `?${qs}` : ''}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setComponents(data);
            })
            .catch(err => {
                console.error('Error fetching CRD components:', err);
                setAlert({ show: true, message: 'Failed to fetch CRD component list.', type: 'error' });
                setComponents([]);
            })
            .finally(() => setLoadingComponents(false));
    }, [siteFilter]);

    useEffect(() => {
        fetchComponents();
    }, [fetchComponents]);

    useEffect(() => {
        setSelectedFamilyId('');
        setComponentSearch('');
        setComponentRows([]);
        setUsageMax(null);
    }, [siteFilter]);

    const fetchComponentRows = useCallback(() => {
        if (!selectedFamilyId) {
            setComponentRows([]);
            return;
        }
        setLoadingRows(true);
        const params = new URLSearchParams();
        params.append('family_id', selectedFamilyId);
        if (siteFilter) params.append('site_name', siteFilter);

        api.get(`/crd-all/?${params.toString()}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setComponentRows(data);
            })
            .catch(err => {
                console.error('Error fetching high time component data:', err);
                setAlert({ show: true, message: 'Failed to fetch CRD component data.', type: 'error' });
                setComponentRows([]);
            })
            .finally(() => setLoadingRows(false));
    }, [selectedFamilyId, siteFilter]);

    useEffect(() => {
        fetchComponentRows();
    }, [fetchComponentRows]);

    const filteredComponents = useMemo(() => {
        const filtered = filterComponents(components, componentSearch);
        if (selectedFamilyId && !filtered.some(c => c.family_id === selectedFamilyId)) {
            const selected = components.find(c => c.family_id === selectedFamilyId);
            if (selected) return [selected, ...filtered];
        }
        return filtered;
    }, [components, componentSearch, selectedFamilyId]);

    const selectedComponent = useMemo(
        () => components.find(c => c.family_id === selectedFamilyId),
        [components, selectedFamilyId],
    );
    const selectedLabel = selectedFamilyId
        ? (selectedComponent?.nomen || selectedFamilyId)
        : '';

    const chartRows = useMemo(() => {
        const canonicalNomen = selectedComponent?.nomen;
        if (!canonicalNomen) return componentRows;
        return componentRows.map(row => ({
            ...row,
            crd_nomen: row.nomen,
            nomen: canonicalNomen,
        }));
    }, [componentRows, selectedComponent]);

    const chartData = useMemo(
        () => processHighTimeComponentData(chartRows),
        [chartRows],
    );

    const sliderMax = useMemo(() => {
        if (chartData.items.length === 0) return 6000;
        const maxRemaining = Math.max(...chartData.items.map(i => i.remaining), 0);
        return Math.max(USAGE_REMAINING_STEP, Math.ceil(maxRemaining / USAGE_REMAINING_STEP) * USAGE_REMAINING_STEP);
    }, [chartData.items]);

    useEffect(() => {
        setUsageMax(sliderMax);
    }, [selectedFamilyId, sliderMax]);

    const effectiveMax = usageMax == null ? sliderMax : usageMax;
    const isFullRange = effectiveMax >= sliderMax;

    const displayedChartData = useMemo(() => {
        if (isFullRange) return chartData;
        const zoomedItems = chartData.items.filter(item => item.remaining <= effectiveMax);
        return buildHighTimeWaterfallChart(zoomedItems);
    }, [chartData, effectiveMax, isFullRange]);

    const zoomedCount = displayedChartData.items.length;
    const tableItems = displayedChartData.items;

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: selectedLabel
                    ? (isFullRange
                        ? `${selectedLabel} — Usage Remaining by Aircraft`
                        : `${selectedLabel} — Usage Remaining (≤ ${effectiveMax.toLocaleString()} hrs)`)
                    : 'Usage Remaining by Aircraft',
                font: { size: 16, weight: 'bold' },
                color: theme === 'dark' ? '#e0e0e0' : '#333',
            },
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label(context) {
                        const item = displayedChartData.items[context.dataIndex];
                        if (!item) return '';
                        const lines = [
                            `Usage Remaining: ${item.remaining.toFixed(1)}`,
                            `Part: ${item.pn || 'N/A'}`,
                            `Nomenclature: ${item.nomen || 'N/A'}`,
                        ];
                        if (item.crdNomen && item.crdNomen !== item.nomen) {
                            lines.push(`CRD Nomenclature: ${item.crdNomen}`);
                        }
                        lines.push(
                            `Position: ${item.posCd || 'N/A'}`,
                            `Serial: ${item.serno || 'N/A'}`,
                            `Scheduled: ${item.schdExpndtr ?? 'N/A'}`,
                        );
                        return lines;
                    },
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'BUNO (Position)',
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                    font: { size: 10 },
                },
                grid: { color: theme === 'dark' ? '#444' : 'rgba(0,0,0,0.1)' },
            },
            y: {
                ...(isFullRange ? {} : { max: Math.max(effectiveMax, USAGE_REMAINING_STEP) }),
                title: {
                    display: true,
                    text: 'Usage Remaining',
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                },
                ticks: { color: theme === 'dark' ? '#e0e0e0' : '#333' },
                grid: { color: theme === 'dark' ? '#444' : 'rgba(0,0,0,0.1)' },
            },
        },
    }), [theme, displayedChartData.items, selectedLabel, isFullRange, effectiveMax]);

    const legendBands = useMemo(
        () => REMAINING_BAND_ORDER.filter(band => chartData.bandColors[band]),
        [chartData.bandColors],
    );

    return (
        <div style={styles.section}>
            <CustomAlert
                show={alert.show}
                message={alert.message}
                type={alert.type}
                theme={theme}
                onClose={() => setAlert({ show: false, message: '', type: '' })}
            />

            <div style={styles.pageHeader}>
                <HamburgerMenu theme={theme} />
                <Link to="/reports" style={styles.navLink}>&larr; Back to Reports</Link>
            </div>

            <div style={styles.card}>
                <h2 style={styles.title}>High Time Component</h2>
                <p style={styles.subtitle}>
                    Select a component family to see usage remaining across the fleet. Related old and new part numbers are grouped together and sorted lowest remaining first.
                </p>

                <div style={styles.controls}>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.select}
                        aria-label="Site filter"
                    >
                        <option value="">All Sites</option>
                        {sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <input
                        type="search"
                        value={componentSearch}
                        onChange={(e) => setComponentSearch(e.target.value)}
                        placeholder="Search part number or nomenclature…"
                        style={styles.searchInput}
                        aria-label="Search components"
                    />
                    <select
                        value={selectedFamilyId}
                        onChange={(e) => setSelectedFamilyId(e.target.value)}
                        style={styles.componentSelect}
                        aria-label="Component"
                        disabled={loadingComponents}
                    >
                        <option value="">
                            {loadingComponents ? 'Loading components…' : 'Select a component'}
                        </option>
                        {filteredComponents.map(component => (
                            <option key={component.family_id} value={component.family_id}>
                                {component.nomen}
                                {Array.isArray(component.pns) && component.pns.length > 1
                                    ? ` (${component.pns.length} PNs)`
                                    : ''}
                                {' '}({component.installation_count}
                                {component.min_remaining != null
                                    ? `, min ${Number(component.min_remaining).toFixed(1)} hrs`
                                    : ''}
                                )
                            </option>
                        ))}
                    </select>
                    <button type="button" onClick={selectedFamilyId ? fetchComponentRows : fetchComponents} style={styles.button}>
                        Refresh Data
                    </button>
                </div>

                {loadingComponents && !selectedFamilyId ? (
                    <div style={styles.loading}><Spinner /></div>
                ) : components.length === 0 ? (
                    <div style={styles.empty}>
                        No CRD All data available. Upload a CRD All (FOUO CRD) file on the Upload Data page.
                    </div>
                ) : !selectedFamilyId ? (
                    <div style={styles.empty}>
                        Select a component to view the waterfall. {filteredComponents.length} component
                        {filteredComponents.length === 1 ? '' : 's'} available
                        {componentSearch ? ' matching the search' : ''}.
                    </div>
                ) : loadingRows ? (
                    <div style={styles.loading}><Spinner /></div>
                ) : chartData.items.length === 0 ? (
                    <div style={styles.empty}>
                        No installations with usage remaining for this component at the selected site.
                    </div>
                ) : (
                    <>
                        <div style={styles.zoomControl}>
                            <div style={styles.zoomHeader}>
                                <span style={styles.zoomLabel}>Chart zoom — max usage remaining</span>
                                <span style={styles.zoomValue}>
                                    {isFullRange
                                        ? `${sliderMax.toLocaleString()} (full view)`
                                        : `≤ ${effectiveMax.toLocaleString()} hrs · ${zoomedCount} installation${zoomedCount === 1 ? '' : 's'}`}
                                </span>
                            </div>
                            <input
                                type="range"
                                min={USAGE_REMAINING_MIN}
                                max={sliderMax}
                                step={USAGE_REMAINING_STEP}
                                value={effectiveMax}
                                onChange={(e) => setUsageMax(Number(e.target.value))}
                                style={styles.zoomSlider}
                                aria-label="Chart zoom max usage remaining"
                            />
                            <div style={styles.zoomScale}>
                                <span>0 (zoom in)</span>
                                <span>{sliderMax.toLocaleString()} (full view)</span>
                            </div>
                            <p style={styles.zoomHint}>
                                Slide left to zoom in and show only installations at or below the selected hours remaining.
                            </p>
                        </div>

                        {displayedChartData.items.length === 0 ? (
                            <div style={styles.empty}>
                                No installations with ≤ {effectiveMax.toLocaleString()} hrs remaining. Slide right to widen the view.
                            </div>
                        ) : (
                            <div style={styles.chartContainer}>
                                <Bar data={displayedChartData} options={chartOptions} />
                            </div>
                        )}

                        {legendBands.length > 0 && (
                            <div style={styles.legendWrap}>
                                {legendBands.map(band => {
                                    const colors = chartData.bandColors[band] || REMAINING_BAND_COLORS[band];
                                    return (
                                        <div key={band} style={styles.legendItem}>
                                            <div style={{
                                                ...styles.legendSwatch,
                                                backgroundColor: colors.bg,
                                                border: `2px solid ${colors.border}`,
                                            }} />
                                            <span>{REMAINING_BAND_LABELS[band]}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {tableItems.length > 0 && (
                            <>
                                <h3 style={{ ...styles.title, fontSize: '16px', marginTop: '24px' }}>
                                    {isFullRange
                                        ? `Component Detail (${tableItems.length} installation${tableItems.length === 1 ? '' : 's'})`
                                        : `Usage Remaining (≤ ${effectiveMax.toLocaleString()}) — ${tableItems.length} installation${tableItems.length === 1 ? '' : 's'}`}
                                </h3>
                                <div style={styles.tableWrap}>
                                    <table style={styles.detailTable}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>BUNO</th>
                                                <th style={styles.th}>Status</th>
                                                <th style={styles.th}>Modex</th>
                                                <th style={styles.th}>Site</th>
                                                <th style={styles.th}>Position</th>
                                                <th style={styles.th}>Remaining</th>
                                                <th style={styles.th}>Scheduled</th>
                                                <th style={styles.th}>Part Number</th>
                                                <th style={styles.th}>Nomenclature</th>
                                                <th style={styles.th}>Serial</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableItems.map(item => {
                                                const acStatus = statusByBuno[String(item.buno).trim()] || '';
                                                return (
                                                    <tr key={item.id != null ? item.id : item.label}>
                                                        <td style={styles.td}>
                                                            <Link
                                                                to={aircraftComponentLifePath(item.buno, item.siteName)}
                                                                style={styles.navLink}
                                                                title="View this BUNO"
                                                            >
                                                                {item.buno}
                                                            </Link>
                                                        </td>
                                                        <td style={{
                                                            ...styles.td,
                                                            fontWeight: 'bold',
                                                            color: acStatus ? getStatusColor(acStatus) : styles.td.color,
                                                        }}>
                                                            {acStatus || '—'}
                                                        </td>
                                                        <td style={styles.td}>{item.modex || '—'}</td>
                                                        <td style={styles.td}>{item.siteName || '—'}</td>
                                                        <td style={styles.td}>{item.posCd || '—'}</td>
                                                        <td style={styles.td}>{item.remaining.toFixed(1)}</td>
                                                        <td style={styles.td}>{formatHours(item.schdExpndtr)}</td>
                                                        <td style={styles.td}>{item.pn || '—'}</td>
                                                        <td style={styles.td}>{item.nomen || '—'}</td>
                                                        <td style={styles.td}>{item.serno || '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default HighTimeComponentPage;
